import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "@/components/AdminShell";
import api from "@/lib/api";

export default function AdminCosts() {
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState(0);
  const [savedLimit, setSavedLimit] = useState(null);

  useEffect(() => {
    document.title = "Admin · Costs";
    (async () => {
      const [c, a] = await Promise.all([
        api.get("/admin/costs"),
        api.get("/admin/costs/alert"),
      ]);
      setData(c.data);
      setLimit(a.data?.daily_limit_gbp ?? 0);
      setSavedLimit(a.data?.daily_limit_gbp ?? 0);
    })();
  }, []);

  const saveAlert = async () => {
    await api.post("/admin/costs/alert", { daily_limit_gbp: Number(limit) });
    setSavedLimit(Number(limit));
  };

  if (!data) {
    return (
      <AdminShell>
        <div className="text-xs text-zinc-500">Loading…</div>
      </AdminShell>
    );
  }

  const maxDaily = Math.max(
    1,
    ...data.daily.map((d) =>
      Object.entries(d)
        .filter(([k]) => k !== "date")
        .reduce((s, [, v]) => s + (v || 0), 0)
    )
  );

  return (
    <AdminShell>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">Costs</div>
          <h1 className="display mt-1 text-3xl font-black tracking-tight">LLM spend</h1>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
            Daily alert threshold:
          </span>
          <input
            type="number"
            min={0}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            data-testid="cost-alert-input"
            className="bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1 text-xs w-24"
          />
          <button
            onClick={saveAlert}
            data-testid="cost-alert-save"
            className="bg-white text-black text-xs px-2 py-1 rounded-[3px] hover:bg-zinc-200"
          >
            Save
          </button>
          {savedLimit !== null && (
            <span className="text-[10px] text-zinc-500 mono">current: £{savedLimit}</span>
          )}
        </div>
      </div>

      {/* Stacked bar chart, CSS-only */}
      <section className="bg-zinc-900/60 border border-zinc-800 rounded-[4px] p-4" data-testid="costs-daily">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          Daily spend · last 30 days
        </div>
        <div className="mt-4 h-40 flex items-end gap-1">
          {data.daily.map((d, i) => {
            const parts = Object.entries(d).filter(([k]) => k !== "date");
            const total = parts.reduce((s, [, v]) => s + (v || 0), 0);
            return (
              <div key={i} className="flex-1 flex flex-col justify-end" title={`${d.date}: £${(total / 100).toFixed(2)}`}>
                {parts.map(([eng, v]) => (
                  <div
                    key={eng}
                    className={`${engineColor(eng)}`}
                    style={{ height: `${((v || 0) / maxDaily) * 100}%` }}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </section>

      <section className="mt-4 grid md:grid-cols-3 gap-3">
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px]">
          <div className="px-3 py-2 border-b border-zinc-800 mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            Per-engine
          </div>
          <table className="w-full text-xs" data-testid="costs-per-engine">
            <thead className="bg-zinc-900">
              <tr className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                <th className="text-left p-2">Engine</th>
                <th className="text-right p-2">Calls</th>
                <th className="text-right p-2">Cost</th>
                <th className="text-right p-2">Avg £</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {data.per_engine.map((e) => (
                <tr key={e.engine}>
                  <td className="p-2">
                    <span
                      className={`inline-block w-2 h-2 mr-2 ${engineColor(e.engine)}`}
                    />
                    {e.engine}
                  </td>
                  <td className="p-2 text-right mono tabular-nums">{e.calls}</td>
                  <td className="p-2 text-right mono tabular-nums">£{e.cost_gbp}</td>
                  <td className="p-2 text-right mono tabular-nums">£{e.avg_cost_per_call_gbp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px] md:col-span-2">
          <div className="px-3 py-2 border-b border-zinc-800 mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            Top 50 users
          </div>
          <div className="max-h-[360px] overflow-auto">
            <table className="w-full text-xs" data-testid="costs-top-users">
              <thead className="bg-zinc-900 sticky top-0">
                <tr className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                  <th className="text-left p-2">User</th>
                  <th className="text-left p-2">Plan</th>
                  <th className="text-right p-2">Cost</th>
                  <th className="text-right p-2">MRR</th>
                  <th className="text-right p-2">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {data.top_users.map((r) => (
                  <tr key={r.user_id}>
                    <td className="p-2">
                      <Link to={`/admin/users/${r.user_id}`} className="hover:underline">
                        {r.email || r.user_id}
                      </Link>
                    </td>
                    <td className="p-2">{r.plan}</td>
                    <td className="p-2 text-right mono tabular-nums">£{r.cost_gbp}</td>
                    <td className="p-2 text-right mono tabular-nums">£{r.mrr_contribution_gbp}</td>
                    <td
                      className={`p-2 text-right mono tabular-nums ${
                        r.margin_gbp < 0 ? "text-rose-300" : ""
                      }`}
                    >
                      £{r.margin_gbp.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="mt-4 bg-zinc-900/60 border border-zinc-800 rounded-[4px]" data-testid="costs-negative-users">
        <div className="px-3 py-2 border-b border-zinc-800 mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 flex items-center justify-between">
          <span>Cost-negative users (LLM cost &gt; MRR)</span>
          <span className="text-rose-300">{data.cost_negative_users.length}</span>
        </div>
        {data.cost_negative_users.length === 0 ? (
          <div className="p-3 text-xs text-zinc-500">
            No cost-negative users right now — margins look healthy.
          </div>
        ) : (
          <table className="w-full text-xs">
            <tbody className="divide-y divide-zinc-800">
              {data.cost_negative_users.map((r) => (
                <tr key={r.user_id}>
                  <td className="p-2">
                    <Link to={`/admin/users/${r.user_id}`} className="hover:underline">
                      {r.email}
                    </Link>
                  </td>
                  <td className="p-2">{r.plan}</td>
                  <td className="p-2 text-right mono tabular-nums">cost £{r.cost_gbp}</td>
                  <td className="p-2 text-right mono tabular-nums">mrr £{r.mrr_contribution_gbp}</td>
                  <td className="p-2 text-right mono tabular-nums text-rose-300">
                    £{r.margin_gbp.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </AdminShell>
  );
}

function engineColor(eng) {
  return {
    openai: "bg-emerald-400",
    anthropic: "bg-amber-400",
    gemini: "bg-sky-400",
    perplexity: "bg-purple-400",
    google_aio: "bg-zinc-400",
  }[eng] || "bg-zinc-600";
}
